import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { ZONE_LABELS } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", siret: "", adresse: "", is_association: false, rapport_assujettissement: 100, zone: "metropole", periodicite: "annuelle", taxe_n1: 0, contact_email: "", smic_mensuel: 1823.03 };

export const CompanyForm = ({ company, onSaved, onCancel }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(company ? { ...EMPTY, ...company } : EMPTY); }, [company]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("La raison sociale est obligatoire");
    setSaving(true);
    try {
      const payload = { name: form.name, siret: form.siret, adresse: form.adresse, is_association: form.is_association, rapport_assujettissement: Number(form.rapport_assujettissement), zone: form.zone, periodicite: form.periodicite, taxe_n1: Number(form.taxe_n1), contact_email: form.contact_email.trim(), smic_mensuel: Number(form.smic_mensuel) || 0 };
      const { data } = company ? await api.put(`/companies/${company.id}`, payload) : await api.post("/companies", payload);
      toast.success(company ? "Paramètres enregistrés" : "Entreprise créée");
      onSaved(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-5" data-testid="company-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Raison sociale *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="company-settings-name-input" /></div>
        <div><Label>SIRET</Label><Input value={form.siret} onChange={(e) => set("siret", e.target.value)} placeholder="14 chiffres" className="font-mono" data-testid="company-settings-siret-input" /></div>
        <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => set("adresse", e.target.value)} data-testid="company-settings-adresse-input" /></div>
        <div>
          <Label>E-mail de contact (rappels d'échéances)</Label>
          <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="compta@entreprise.fr" data-testid="company-settings-contact-email-input" />
          <p className="text-xs text-muted-foreground mt-1">Reçoit les rappels 10 jours avant chaque échéance, en plus du titulaire du compte.</p>
        </div>
        <div>
          <Label>SMIC mensuel brut de référence (€)</Label>
          <Input type="number" min="0" step="0.01" className="font-mono" value={form.smic_mensuel} onChange={(e) => set("smic_mensuel", e.target.value)} data-testid="company-settings-smic-input" />
          <p className="text-xs text-muted-foreground mt-1">Sert au calcul de l'exonération apprentis (11 % du SMIC, 20 % en DOM) si l'effectif dépasse 10 salariés.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label>Rapport d'assujettissement (%)</Label>
          <Input type="number" min="0" max="100" step="0.01" className="font-mono" value={form.rapport_assujettissement} onChange={(e) => set("rapport_assujettissement", e.target.value)} data-testid="company-settings-rapport-input" />
          <p className="text-xs text-muted-foreground mt-1">Part du chiffre d'affaires non soumise à la TVA. 100 % si non assujetti à la TVA.</p>
        </div>
        <div>
          <Label>Zone géographique</Label>
          <Select value={form.zone} onValueChange={(v) => set("zone", v)}>
            <SelectTrigger data-testid="company-settings-zone-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ZONE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Périodicité des versements</Label>
          <Select value={form.periodicite} onValueChange={(v) => set("periodicite", v)}>
            <SelectTrigger data-testid="company-settings-periodicite-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="annuelle">Annuelle (taxe N-1 &lt; 4 000 €)</SelectItem>
              <SelectItem value="trimestrielle">Trimestrielle (4 000 – 10 000 €)</SelectItem>
              <SelectItem value="mensuelle">Mensuelle (&gt; 10 000 €)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Taxe due au titre de N-1 (€)</Label>
          <Input type="number" min="0" className="font-mono" value={form.taxe_n1} onChange={(e) => set("taxe_n1", e.target.value)} data-testid="company-settings-taxe-n1-input" />
          <p className="text-xs text-muted-foreground mt-1">Sert à recommander la périodicité.</p>
        </div>
        <div className="md:col-span-2 flex items-center gap-3 rounded-md border border-border p-3 bg-muted/60">
          <Switch checked={form.is_association} onCheckedChange={(v) => set("is_association", v)} data-testid="company-settings-association-switch" />
          <div>
            <div className="text-sm font-medium text-foreground">Association loi 1901 / organisme éligible</div>
            <div className="text-xs text-muted-foreground">Abattement annuel (art. 1679 A CGI) : 24 256 € en 2026, 24 041 € en 2025.</div>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="outline" onClick={onCancel} data-testid="company-settings-cancel-button">Annuler</Button>}
        <Button onClick={save} disabled={saving} data-testid="company-settings-save-button">{saving ? "Enregistrement…" : company ? "Enregistrer" : "Créer l'entreprise"}</Button>
      </div>
    </div>
  );
};
